import fetch from 'isomorphic-fetch'
import { API_URL } from 'shared/api'
import { URL_TYPE } from 'shared/components/inputs/url-input'
import log from 'shared/util/log'
import { hasFilter } from './creation-filters'
import { setCreationStatus, STATUS } from './creation-tool'
import { convertFramesToVideo } from './creation-tool-to-video'
import { parseUrl } from 'shared/util/url'

const CREATE_URL = `${API_URL}create`

const jsonToFormData = json => {
    const formData = new FormData()
    for (let key in json) {
        formData.append(key, json[key])
    }
    return formData
}

export const uploadGif = (uploadUrl, data, userToken) => {
    let headers = {
        Accept: '*/*',
    }
    if (userToken) {
        headers['Authorization'] = `Token ${userToken}`
    }
    // api_key is passed via url to auth via kong
    return fetch(`${uploadUrl}?api_key=${data.api_key}`, {
        method: 'POST',
        headers: headers,
        body: jsonToFormData(data),
    })
        .then(response => response.json())
        .then(({ data: { id } }) => id)
}

const uploadVideo = (data, userToken) => {
    let headers = {
        Accept: '*/*',
    }
    if (userToken) {
        headers['Authorization'] = `Token ${userToken}`
    }
    // video endpoint uses hidden not is_hidden
    data.hidden = data.is_hidden
    // api_key is passed via url to auth via kong
    return fetch(`${CREATE_URL}/video?api_key=${data.api_key}`, {
        method: 'POST',
        headers: headers,
        body: jsonToFormData(data),
    })
        .then(response => response.json())
        .then(({ data: { id } }) => id)
}

const getData = (
    {
        apiKey,
        defaultTags,
        isPrivate,
        rating,
        sourceUrl,
        tags,
        featuredTags,
        username,
        isVideoUpload,
        videoTitle,
        videoDescription,
        videoPreviewStart,
        videoPreviewDuration,
        channelId,
        creationLocation,
    },
    permissions = {}
) => {
    let data = {
        api_key: apiKey,
        tags: [...tags, ...defaultTags].join(','),
        featured_tags: featuredTags.join(','),
        source_post_url: sourceUrl,
        is_hidden: isPrivate ? 1 : 0,
        rating: !isVideoUpload || (permissions && permissions.rate_upload_videos) ? rating : null,
        username,
        creation_location: creationLocation,
    }

    if (isVideoUpload) {
        let videoData = {
            title: videoTitle,
            description: videoDescription,
        }

        if (videoPreviewStart && videoPreviewDuration) {
            videoData['start'] = videoPreviewStart.toFixed(1)
            videoData['duration'] = videoPreviewDuration.toFixed(1)
        }

        data = { ...data, ...videoData }
    } else if (channelId) {
        // enables you to save to a channel
        // leaveing video alone...
        data.channel_id = channelId
    }

    return data
}

export const uploadUrl = () => (dispatch, getState) => {
    const {
        creationTool,
        creationTool: { submitUrl, editedFile },
        user: { permissions, userToken },
    } = getState()
    const data = getData(creationTool, permissions)
    data.source_image_url = editedFile
    dispatch(setCreationStatus(STATUS.UPLOADING))
    return uploadGif(submitUrl, data, userToken)
        .then(gifId => {
            dispatch(setCreationStatus(STATUS.COMPLETE))
            return gifId
        })
        .catch(e => {
            log.error(`Failed to upload`, e)
            dispatch(setCreationStatus(STATUS.ERROR))
        })
}

export const upload = file => (dispatch, getState) => {
    const {
        creationTool,
        user: { permissions, userToken },
    } = getState()
    const { submitUrl, hasEdits, isVideoUpload } = creationTool
    dispatch(setCreationStatus(STATUS.UPLOADING))

    const data = getData(creationTool, permissions)
    const isVideoWithNoEdits = file.fileType === 'video' && !hasEdits

    if (file.type === 'file') {
        data.file = file.file
    } else {
        data.source_image_url = file.videoEmbed || file.file
    }

    // unedited uploaded videos need to go through the creation
    // endpoint in order to maintain startTime and duration
    if (!isVideoWithNoEdits && file.type === 'file' && file.fileType === 'video') {
        return uploadVideo(
            {
                ...data,
                start: 0,
                duration: file.duration, // in seconds
                sync: 1, // tells server to keep connection open instead of polling
            },
            userToken
        )
    }

    const uploadUrl = isVideoUpload ? `${submitUrl}/video` : submitUrl

    return uploadGif(uploadUrl, data, userToken)
        .then(gifId => {
            if (!gifId) {
                throw new Error(`No gif Id returned from api`, data)
            }
            dispatch(setCreationStatus(STATUS.COMPLETE))
            return gifId
        })
        .catch(e => {
            log.error(`Failed to upload`, e)
            dispatch(setCreationStatus(STATUS.ERROR))
            throw new Error(`Failed to upload`, e)
        })
}

export default function(frames, jobId, stickers) {
    return function(dispatch, getState) {
        const {
            creationTool,
            creationFilters,
            slideshow,
            user: { permissions, userToken },
        } = getState()
        const { file, editedFile, hasEdits, termsAgreed, channelId, submitUrl, trimData } = creationTool

        if (!termsAgreed) {
            return Promise.reject()
        }

        const data = getData(creationTool, permissions)

        if (channelId) {
            data.channel_id = channelId
        }

        const creationGifs = { associations: [] }
        // If gifmaker on desktop ever goes 3D, Z-coordinate will need to be added. Probably rotation and other fields, too.
        stickers.forEach(({ id, x, y, width, height, start, end }) =>
            creationGifs.associations.push({
                id,
                association_type: 'sticker',
                x,
                y,
                width,
                height,
                start,
                end,
            })
        )

        slideshow.items
            .filter(slide => slide.urlType === URL_TYPE.GIPHY || slide.urlType === URL_TYPE.GIPHY_STARTER)
            .forEach(slide => {
                const parsed = parseUrl(slide.sourceUrl)
                const [, part1, part2] = parsed.pathname.split('/')
                if (part1 === 'media' || part1 === 'gifs' || part1 === 'stickers') {
                    const [sourceGifId] = part2.split('-').slice(-1)
                    creationGifs.associations.push({ id: sourceGifId, association_type: 'background' })
                }
            })

        // creationGifs.associations.push.apply(creationGifs.associations, giphySlides)
        data.creation_gifs = JSON.stringify(creationGifs)

        dispatch(setCreationStatus(STATUS.UPLOADING))

        if (editedFile && editedFile.type === 'backdrop') {
            data.file = editedFile.file
            return uploadGif(submitUrl, data, userToken)
                .then(gifId => {
                    if (!gifId) {
                        throw new Error(`No gif Id returned from api`, data)
                    }
                    dispatch(setCreationStatus(STATUS.COMPLETE))
                    return gifId
                })
                .catch(e => {
                    log.error(`Failed to upload`, e)
                    dispatch(setCreationStatus(STATUS.ERROR))
                })
        }

        if (!hasEdits && file.fileType !== 'slideshow' && !hasFilter(creationFilters)) {
            log.info(`Creation tool upload: unedited file upload`)
            if (file.type === 'file') {
                data.file = file.file
            } else {
                data.source_image_url = file.videoEmbed || file.file
            }
            // unedited uploaded videos need to go through the creation
            // endpoint in order to maintain startTime and duration
            if (file.type === 'file' && file.fileType === 'video') {
                const { duration, startFrame } = trimData
                return uploadVideo(
                    {
                        ...data,
                        start: startFrame / 15,
                        duration: duration / 15, // convert frames to seconds
                        sync: 1, // tells server to keep connection open instead of polling
                    },
                    userToken
                )
            }

            return uploadGif(submitUrl, data, userToken)
                .then(gifId => {
                    if (!gifId) {
                        throw new Error(`No gif Id returned from api`, data)
                    }
                    dispatch(setCreationStatus(STATUS.COMPLETE))
                    return gifId
                })
                .catch(e => {
                    log.error(`Failed to upload`, e)
                    dispatch(setCreationStatus(STATUS.ERROR))
                })
        }
        return convertFramesToVideo({ frames, jobId }).then(blob => {
            data.file = blob
            return uploadGif(submitUrl, data, userToken)
                .then(gifId => {
                    dispatch(setCreationStatus(STATUS.COMPLETE))
                    return gifId
                })
                .catch(e => {
                    log.error(`Failed to upload`, e)
                    dispatch(setCreationStatus(STATUS.ERROR))
                })
        })
    }
}
